<template>
  <v-card>
    <v-container>
      <!-- Documentation button -->
      <DocumentationDialogue class="tw-inline" path="manager/moderation/review" :key="$i18n.locale"/>
      <v-dialog
          v-if="showSolicitation"
          v-model="review.solicitation"
          max-width="80%"
          @click:outside="closeSolicitationDialog"
      >
        <v-card>
          <v-card-title>
            <span class="sr-title">{{ $t("invitation.tracking.title") }} N° {{ review.solicitation.id }}</span>
          </v-card-title>
          <v-card-text>
            <solicitation-view :solicitation="review.solicitation"></solicitation-view>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" small @click="closeSolicitationDialog"
            >{{ $t("invitation.tracking.close") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <span class="sr-title">
        {{ $t("dashboard.reviewsModeration.review.card_title") }}
      </span>

      <!-- Content of the review card -->
      <v-card-text>
        <div>
          <span v-if="review.product">
            <span v-html="$t('reviews.list.product.intro.manager')"></span>
            <b>{{ review.product.commercial_name }}</b>
          </span>
          <span v-if="review.thematic">
            <span v-html="$t('reviews.list.thematic.intro.manager')"></span>
            <b>{{ review.thematic.thematic }}</b>
          </span>
        </div>

        <v-row>
          <v-col class="tw-flex tw-items-center tw-justify-between">
            <span>
              <v-avatar size="48" v-if="review.user.avatar">
                <img :alt="review.user.user_name" :src="review.user.avatar" />
              </v-avatar>
              <span
                v-if="getName(review).length > 0"
                :title="getUserName(review)"
                class="blue--text"
                v-html="(review.solicitation_id >= 5) ? getName(review) + ' (' + review.user.email + ')' : getName(review)"
              ></span>
              <span class="blue--text" v-else>@{{ getUserName(review) }}</span>
            </span>
            <span>
              <span class="ml-2">
                <template v-if="review.solicitation_id > 5">
                  <span>{{ $t("dashboard.reviewsModeration.review.solicitation") }}</span>
                </template>
                <template v-else>
                  <span v-if="review.solicitation_id === 5">{{ $t("dashboard.reviewsModeration.review.customer_without_solicitation") }}</span>
                  <span v-else>{{ $t("dashboard.reviewsModeration.review.not_solicitation") }}</span>
                </template>
                <b> {{ getOrigin(review) }} ©</b>
              </span>
              <span class="ml-2">{{ review_date }}</span>
              <span class="ml-2">
                <v-icon color="secondary" style="margin-bottom: 3px;" x-small>fa-star</v-icon>
                {{ review.rating }}/5
              </span>
              <span v-if="review.like && review.like.counter > 0" class="ml-2" x-small>
                <v-icon x-small class="pb-1">fa-thumbs-up</v-icon>
                {{ review.like.counter }}
              </span>
            </span>
          </v-col>
        </v-row>

        <!-- Here is the form to change/update to the current review -->
        <div class="mt-4">
          <div v-if="edit">
            <v-textarea
              v-model="review.message"
              :label="$t('dashboard.reviewsModeration.review.message')"
              auto-grow
              class="mt-6"
              name="message"
              no-resize
            />
          </div>
          <div v-else>
            <u>{{ $t('dashboard.reviewsModeration.review.message') }}</u>
            <p v-if="review.message"
              style="white-space: pre-wrap; max-height: 200px; overflow-y: scroll; padding:5px 10px; background-color: #e7e9eb; border-radius: 3px 0 0 3px;"
              v-text="review.message"
            />
          </div>
        </div>
        <div v-if="isOrder" class="mt-4">
          <div style="width:45%;float:left;" v-if="hasOrderDate">
            <u>{{ $t('dashboard.reviewsModeration.review.order_date') }}</u>
            <p style="padding:5px 10px; background-color: #e7e9eb;" v-text="getOrderDate"/>
          </div>
          <div style="width:45%;float:left;" v-if="hasOrderNumber">
            <u>{{ $t('dashboard.reviewsModeration.review.order_number') }}</u>
            <p style="padding:5px 10px; background-color: #e7e9eb;" v-text="getOrderNumber"/>
          </div>
        </div>
        <div class="mb-4">
          <span>
            <span></span>
            <span v-if="updatedDate" :title="review.updated_at" class="ml-1 mt-1 tw-text-sm">
              {{ $t("dashboard.reviewsModeration.review.update_date", { date: updateDate }) }}
            </span>
          </span>
        </div>
        <div v-if="review.confidence < 10" style="float:right;">
          <b>{{ $t("dashboard.reviewsModeration.response.report.label") }} : </b>
          <v-chip color="warning" text-color="black" x-small>
            {{ $t("dashboard.reviewsModeration.response.report." + review.confidence) }}
          </v-chip>
        </div>

        <div>
          <!-- Link to the review -->
          <v-btn @click="openExternalLink()"
                 x-small>
            {{ $t("dashboard.reviewsModeration.review.external") }}
          </v-btn>
          <v-btn @click="generateModerationLink"
            class="ml-2"
            v-if="!withoutToken"
            :title="$t('dashboard.reviewsModeration.review.link')"
            x-small
          >
            {{ $t("dashboard.reviewsModeration.review.link") }}
          </v-btn>
          <v-btn @click="generateModerationLinkMailTo"
            class="ml-2"
            v-if="!withoutToken"
            :title="$t('dashboard.reviewsModeration.review.mailto')"
            x-small
          >
            {{ $t("dashboard.reviewsModeration.review.mailto") }}
          </v-btn>
          <v-btn @click="showSolicitation=true"
                 class="ml-2"
                 v-if="review.solicitation_id > 0"
                 :title="$t('dashboard.reviewsModeration.review.viewSolicitation')"
                 x-small
          >
            {{ $t("dashboard.reviewsModeration.review.viewSolicitation") }}
          </v-btn>
        </div>
      </v-card-text>

      <template v-if="belongsToCurrentUser">
        <div v-if="edit" class="tw-flex tw-flex-wrap">
          <v-btn class="tw-m-1" small @click="update">
            {{ $t("dashboard.reviewsModeration.review.buttons.update") }}
          </v-btn>
          <v-btn class="tw-m-1" small @click="toggle">
            {{ $t("dashboard.reviewsModeration.review.buttons.cancel") }}
          </v-btn>
          <v-btn class="tw-m-1" color="error" small @click="remove">
            {{ $t("dashboard.reviewsModeration.review.buttons.delete") }}
          </v-btn>
        </div>
        <div v-else>
          <v-btn class="tw-m-1" small @click="toggle">
            {{ $t("dashboard.reviewsModeration.review.buttons.edit") }}
          </v-btn>
        </div>
      </template>
    </v-container>
  </v-card>
</template>

<script>
import { Types as TypesPublications } from "../../../../store/modules/publications"
import { Types as TypesDialog } from "../../../../store/modules/dialog"
import { Types as TypesSnackbar } from "../../../../store/modules/snackbar"
import { URL_MANAGER } from "@/helpers"
import SolicitationView from "@/components/common/template/SolicitationView";

export default {
  name: "Review",
  components: {SolicitationView},
  props: {
    review: { required: true, default: {} },
    withoutToken: { required: false, default: false },
  },
  data: () => ({
    showSolicitation: false,
    edit: false,
    locked: false,
    moderation_url: "",
    reasons: [],
  }),
  mounted() {
    this.reasons = [
      {
        value: 1,
        text: this.$t("dashboard.reviewsModeration.response.report.1"),
      },
      {
        value: 2,
        text: this.$t("dashboard.reviewsModeration.response.report.2"),
      },
      {
        value: 3,
        text: this.$t("dashboard.reviewsModeration.response.report.3"),
      },
      {
        value: 4,
        text: this.$t("dashboard.reviewsModeration.response.report.4"),
      },
      {
        value: 5,
        text: this.$t("dashboard.reviewsModeration.response.report.5"),
      },
      {
        value: 6,
        text: this.$t("dashboard.reviewsModeration.response.report.6"),
      },
      {
        value: 7,
        text: this.$t("dashboard.reviewsModeration.response.report.7"),
      },
      {
        value: 8,
        text: this.$t("dashboard.reviewsModeration.response.report.8"),
      },
      {
        value: 9,
        text: this.$t("dashboard.reviewsModeration.response.report.9"),
      },
    ]
  },
  beforeDestroy() {
    if (this.locked) {
      this.free().then(() => {
        this.locked = false
      })
    }
  },
  computed: {
    isOrder() {
      return this.review.from_order===1
    },
    hasOrderDate() {
      if(this.review.from_order===1) {
        if(this.review.details_json) {
          let details = JSON.parse(this.review.details_json)
          return details.hasOwnProperty('order_date')
        }
      }
      return false
    },
    getOrderDate() {
      if(this.review.details_json) {
        let details = JSON.parse(this.review.details_json)
        if(details.hasOwnProperty('order_date')) {
          return details.order_date
        }
      }
      return '-'
    },
    hasOrderNumber() {
      if(this.review.from_order===1) {
        if(this.review.details_json) {
          let details = JSON.parse(this.review.details_json)
          return details.hasOwnProperty('order_number')
        }
      }
      return false
    },
    getOrderNumber() {
      if(this.review.details_json) {
        let details = JSON.parse(this.review.details_json)
        if(details.hasOwnProperty('order_number')) {
          return details.order_number
        }
      }
      return '-'
    },
    updatedDate() {
      let created = new Date(this.review.created_at)
      let updated = new Date(this.review.updated_at)
      return updated.getTime() - created.getTime() > 1000 * 60 * 2
    },
    mailto() {
      const subject = this.$t("dashboard.reviewsModeration.review.subject")
      const body = this.$t("dashboard.reviewsModeration.review.body", {
        url: this.moderation_url,
      })
      return `mailto:?subject=${subject}&body=${body}`
    },
    review_date() {
      let dt = new Date(this.review.created_at)
      return dt.toLocaleDateString("fr-fr")
    },
    belongsToCurrentUser() {
      return this.review.hasOwnProperty("user")
        ? this.$store.state.auth.user.user_name === this.review.user.user_name
        : false
    },
    updateDate() {
      let dt = new Date(this.review.updated_at)
      return dt.toLocaleDateString("fr-fr")
    },
  },
  methods: {
    closeSolicitationDialog() {
      this.showSolicitation = false
    },
    openExternalLink() {
      window.open(this.review.url.full_path, "_blank")
    },
    getUserName(publication) {
      const { user_name } = publication.user
      const semicolonPos = user_name.indexOf(":")
      if (semicolonPos > 0) {
        return ""
      } else {
        return `${user_name}`
      }
    },
    getName(publication) {
      const { first_name } = publication.user
      const { last_name } = publication.user
      let name = ""
      if (first_name) name = name + " " + first_name
      if (last_name) name = name + " " + last_name
      return name.trim()
    },
    displayOrigin(publication) {
      return this.$t("origin.source") + this.getOrigin(publication)
    },
    getOrigin(publication) {
      const securating = "Securating"
      const { user_name } = publication.user
      const semicolonPos = user_name.indexOf(":")
      if (semicolonPos > 0) {
        const prefix = user_name.substr(0, semicolonPos)

        switch (prefix) {
          case "anonymous":
          case "se":
            return securating
          default:
            return `${this.$t("origin." + prefix)}`
        }
      } else {
        return securating
      }
    },
    generateModerationLink() {
      this.$axios
        .get("office/manager/review/token", {
          params: {
            id: this.review.id,
            database: this.review.database,
          },
        })
        .then(response => {
          this.moderation_url = `https://${URL_MANAGER}/moderation/${response.data.data}`
          this.$copyText(this.moderation_url).then(() => {
            this.$store.dispatch(TypesSnackbar.actions.PUSH_MESSAGE, {
              lines: this.$t("dashboard.reviewsModeration.review.copied"),
              config: { timeout: 1000 * 6, color: "success" },
            })
          })
        })
    },
    generateModerationLinkMailTo() {
      this.$axios
        .get("office/manager/review/token", {
          params: {
            id: this.review.id,
            database: this.review.database,
          },
        })
        .then(response => {
          this.moderation_url = `https://${URL_MANAGER}/moderation/${response.data.data}`
          window.open(this.mailto, "_self")
          this.$copyText(this.moderation_url).then(() => {
            this.$store.dispatch(TypesSnackbar.actions.PUSH_MESSAGE, {
              lines: this.$t("dashboard.reviewsModeration.review.copied"),
              config: { timeout: 1000 * 6, color: "success" },
            })
          })
        })
    },
    update() {
      this.$axios.patch("office/manager/review", { review: this.review }).then(response => {
        let reviews = [...this.$store.state.publications.reviews]
        const index = reviews.findIndex(r => r.id === this.review.id && r.database === this.review.database)

        reviews[index] = response.data.data

        // Dispatch changes
        this.$store.dispatch(TypesPublications.actions.REVIEWS, reviews)
        this.free().then(() => {
          this.edit = false
          this.locked = false
        })
      })
    },
    remove() {
      this.$store.dispatch(TypesDialog.actions.CONFIRM, {
        title: this.$t("dashboard.reviewsModeration.dialogs.remove.title"),
        message: this.$t("dashboard.reviewsModeration.dialogs.remove.text"),
        callback: () => {
          this.free().then(() => {
            this.edit = false
            this.locked = false
            this.$axios.delete("office/manager/review", {
              data: { review: this.review },
            })
            this.$emit("remove")
          })
        },
      })
    },
    toggle() {
      if (this.edit === false) {
        this.set().then(() => {
          this.edit = true
          this.locked = true
        })
      } else {
        this.$store.dispatch(TypesDialog.actions.CONFIRM, {
          title: this.$t("dashboard.reviewsModeration.dialogs.cancel.title"),
          message: this.$t("dashboard.reviewsModeration.dialogs.cancel.text"),
          callback: () => {
            this.free().then(() => {
              this.edit = false
              this.locked = false
              this.$emit("rollback")
            })
          },
        })
      }
    },
    set() {
      return this.$axios.post("office/common/deadlocks/set", {
        publication_id: this.review.id,
        database: this.review.database,
      })
    },
    free() {
      return this.$axios.post("office/common/deadlocks/free", {
        publication_id: this.review.id,
        database: this.review.database,
      })
    },
  },
}
</script>
